import {
    getEntries,
    addEntry as addEntryAPI,
    addFeedback as addFeedbackAPI,
    getDecks,
    getCards,
    createDeck as createDeckAPI,
    updateDeck as updateDeckAPI,
    deleteDeck as deleteDeckAPI,
    createCard as createCardAPI,
    updateCard as updateCardAPI,
    deleteCard as deleteCardAPI,
    getSrsCards,
    submitSrsAnswer,
} from '@/clients/api';
import { Models, sentenceCorrector } from '../../clients/llm';
import { JournalContent, JournalEntry } from '@common/models/journal';
import { Card, CardDeck } from '@common/models/cards';
import { CreateDeckRequest } from '@common/models/api';
import { SrsCard, SrsAnswerRequest } from '@common/models/srs';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { User } from '@supabase/supabase-js';

export interface AppState {
    journalEntries: JournalEntry[];
    model: Models;
    user: User | null;
    decks: CardDeck[];
    cardsByDeckId: Record<string, Card[]>;
    enabledDecks: string[];
    showDecksView: boolean;
    currentSRSCards: SrsCard[];
}

const initialState: AppState = {
    journalEntries: [],
    model: 'claude-sonnet-4-6',
    user: null,
    decks: [],
    cardsByDeckId: {},
    enabledDecks: [],
    showDecksView: false,
    currentSRSCards: [],
};

export const addJournalEntry = createAsyncThunk(
    'app/addJournalEntry',
    async (entry: string, { dispatch, getState }) => {
        const state = getState() as { app: AppState }; // Access the state
        const model = state.app.model; // Get the model value
        const userId = state.app.user?.id;

        if (!userId) {
            throw new Error('User not authenticated');
        }

        const tempId = `temp-${Date.now()}`;
        const temporaryEntry: JournalEntry = {
            id: tempId,
            content: { entry },
            created_at: new Date().toISOString(),
            isTemporary: true,
        };

        // Add temporary entry to local state immediately
        dispatch(appSlice.actions.addEntry(temporaryEntry));
        let response: JournalEntry;

        try {
            // Create entry object for backend (without temp fields)
            const entryForBackend = {
                entry,
            } as JournalContent;

            // Save entry to backend and get the returned entry
            response = await addEntryAPI(userId, entryForBackend);
            console.log('Backend response:', response);

            // Replace temporary entry with backend response
            dispatch(
                appSlice.actions.replaceTemporaryEntry({
                    tempId,
                    newEntry: response,
                }),
            );
        } catch (error) {
            // If saving fails, remove the temporary entry from local state
            dispatch(appSlice.actions.removeEntry(tempId));
            throw error;
        }

        // Get feedback for the entry
        dispatch(getFeedbackForEntry(response));
    },
);

export const getFeedbackForEntry = createAsyncThunk(
    'app/addJournalEntry',
    async (entry: JournalEntry, { dispatch, getState }) => {
        const state = getState() as { app: AppState }; // Access the state
        const model = state.app.model; // Get the model value

        // Get feedback from LLM
        const feedback = await sentenceCorrector(entry.content.entry, model);

        // Add feedback to local state
        dispatch(appSlice.actions.addFeedback({ id: entry.id, feedback }));

        // Save feedback to backend
        // TODO: do this in the first call to the sentenceCorrector
        try {
            await addFeedbackAPI(entry.id, feedback);
        } catch (feedbackError) {
            console.error('Failed to save feedback to backend:', feedbackError);
        }
    },
);

export const loadJournalEntries = createAsyncThunk('app/loadJournalEntries', async () => {
    const response = await getEntries();
    return response.entries;
});

export const loadDecks = createAsyncThunk('app/loadDecks', async (_, { dispatch }) => {
    const response = await getDecks();
    for (const deck of response.decks) {
        dispatch(loadCardsByDeckId(deck.id));
    }
    return response.decks;
});

export const loadCardsByDeckId = createAsyncThunk('app/loadCardsByDeckId', async (deckId: string) => {
    const response = await getCards(deckId);
    return { deckId, cards: response.cards };
});

// Deck CRUD async thunks
export const createDeckThunk = createAsyncThunk('app/createDeck', async (deck: CreateDeckRequest, { dispatch }) => {
    const newDeck = await createDeckAPI(deck);
    dispatch(appSlice.actions.addDeck(newDeck));
    return newDeck;
});

export const updateDeckThunk = createAsyncThunk(
    'app/updateDeck',
    async ({ deckId, deck }: { deckId: string; deck: CreateDeckRequest }, { dispatch }) => {
        const updatedDeck = await updateDeckAPI(deckId, deck);
        dispatch(appSlice.actions.updateDeck(updatedDeck));
        return updatedDeck;
    },
);

export const deleteDeckThunk = createAsyncThunk('app/deleteDeck', async (deckId: string, { dispatch }) => {
    await deleteDeckAPI(deckId);
    dispatch(appSlice.actions.removeDeck(deckId));
    return deckId;
});

// Card CRUD async thunks
export const createCardThunk = createAsyncThunk(
    'app/createCard',
    async (card: Omit<Card, 'id' | 'created_at' | 'type'>, { dispatch }) => {
        const newCard = await createCardAPI(card);
        dispatch(appSlice.actions.addCard(newCard));
        return newCard;
    },
);

export const updateCardThunk = createAsyncThunk(
    'app/updateCard',
    async ({ cardId, card }: { cardId: string; card: Pick<Card, 'front' | 'back'> }, { dispatch }) => {
        const updatedCard = await updateCardAPI(cardId, card);
        dispatch(appSlice.actions.updateCard(updatedCard));
        return updatedCard;
    },
);

export const deleteCardThunk = createAsyncThunk(
    'app/deleteCard',
    async ({ deckId, cardId }: { deckId: string; cardId: string }, { dispatch }) => {
        await deleteCardAPI(cardId);
        dispatch(appSlice.actions.removeCard({ deckId, cardId }));
        return { deckId, cardId };
    },
);

// SRS async thunks
export const loadCurrentSRSCards = createAsyncThunk('app/loadCurrentSRSCards', async (_, { getState }) => {
    const state = getState() as { app: AppState };
    const enabledDeckIds = state.app.enabledDecks;

    if (enabledDeckIds.length === 0) {
        return [];
    }

    const response = await getSrsCards({
        deck_ids: enabledDeckIds,
        limit: 10,
        daily_new_max: 5,
    });
    return response.cards;
});

export const submitSRSAnswer = createAsyncThunk(
    'app/submitSRSAnswer',
    async (answer: SrsAnswerRequest, { dispatch, getState }) => {
        const response = await submitSrsAnswer(answer);

        // Remove the answered card from current SRS cards
        dispatch(appSlice.actions.removeCurrentSRSCard(answer.card_id));

        // If we're running low on cards, load more
        const state = getState() as { app: AppState };
        if (state.app.currentSRSCards.length <= 2) {
            dispatch(loadCurrentSRSCards());
        }

        return response;
    },
);

export const appSlice = createSlice({
    name: 'app',
    initialState,
    reducers: {
        addEntry: (state, action: PayloadAction<JournalEntry>) => {
            state.journalEntries.push(action.payload);
        },
        clearJournal: (state) => {
            state.journalEntries = [];
        },
        updateEntry: (state, action: PayloadAction<JournalEntry>) => {
            console.log('Updating entry:', action.payload);
            const index = state.journalEntries.findIndex((entry) => entry.id === action.payload.id);
            if (index !== -1) {
                state.journalEntries[index] = action.payload;
            } else {
                console.error('Entry not found for update:', action.payload.id);
            }
        },
        addFeedback: (state, action: PayloadAction<{ id: string; feedback: string }>) => {
            const index = state.journalEntries.findIndex((entry) => entry.id === action.payload.id);
            if (index !== -1) {
                state.journalEntries[index].content.feedback = action.payload.feedback;
            } else {
                console.error('Entry not found for adding feedback:', action.payload.id);
            }
        },
        removeEntry: (state, action: PayloadAction<string>) => {
            state.journalEntries = state.journalEntries.filter((entry) => entry.id !== action.payload);
        },
        replaceTemporaryEntry: (state, action: PayloadAction<{ tempId: string; newEntry: JournalEntry }>) => {
            const index = state.journalEntries.findIndex((entry) => entry.id === action.payload.tempId);
            if (index !== -1) {
                state.journalEntries[index] = action.payload.newEntry;
            }
        },
        setModel: (state, action: PayloadAction<Models>) => {
            state.model = action.payload; // Add reducer to update model
        },
        setUser: (state, action: PayloadAction<User | null>) => {
            state.user = action.payload;
        },
        addDeck: (state, action: PayloadAction<CardDeck>) => {
            state.decks.push(action.payload);
            state.enabledDecks.push(action.payload.id);
        },
        updateDeck: (state, action: PayloadAction<CardDeck>) => {
            const index = state.decks.findIndex((deck) => deck.id === action.payload.id);
            if (index !== -1) {
                state.decks[index] = action.payload;
            }
        },
        removeDeck: (state, action: PayloadAction<string>) => {
            state.decks = state.decks.filter((deck) => deck.id !== action.payload);
            if (state.cardsByDeckId) {
                delete state.cardsByDeckId[action.payload];
            }
            state.enabledDecks = state.enabledDecks.filter((id) => id !== action.payload);
        },
        addCard: (state, action: PayloadAction<Card>) => {
            const { deck_id } = action.payload;
            if (!state.cardsByDeckId) {
                state.cardsByDeckId = {};
            }
            if (!state.cardsByDeckId[deck_id]) {
                state.cardsByDeckId[deck_id] = [];
            }
            state.cardsByDeckId[deck_id].push(action.payload);
        },
        updateCard: (state, action: PayloadAction<Card>) => {
            const { deck_id, id } = action.payload;
            if (state.cardsByDeckId[deck_id]) {
                const index = state.cardsByDeckId[deck_id].findIndex((card) => card.id === id);
                if (index !== -1) {
                    state.cardsByDeckId[deck_id][index] = action.payload;
                }
            }
        },
        removeCard: (state, action: PayloadAction<{ deckId: string; cardId: string }>) => {
            const { deckId, cardId } = action.payload;
            if (state.cardsByDeckId[deckId]) {
                state.cardsByDeckId[deckId] = state.cardsByDeckId[deckId].filter((card) => card.id !== cardId);
            }
        },
        toggleDeckEnabled: (state, action: PayloadAction<string>) => {
            const deckId = action.payload;
            if (!state.enabledDecks) {
                state.enabledDecks = [];
            }
            if (state.enabledDecks.includes(deckId)) {
                state.enabledDecks = state.enabledDecks.filter((id) => id !== deckId);
            } else {
                state.enabledDecks.push(deckId);
            }
        },
        setShowDecksView: (state, action: PayloadAction<boolean>) => {
            state.showDecksView = action.payload;
        },
        setCurrentSRSCards: (state, action: PayloadAction<SrsCard[]>) => {
            state.currentSRSCards = action.payload;
        },
        removeCurrentSRSCard: (state, action: PayloadAction<string>) => {
            state.currentSRSCards = state.currentSRSCards.filter((card) => card.id !== action.payload);
        },
    },
    extraReducers: (builder) => {
        builder.addCase(addJournalEntry.rejected, (_, action) => {
            console.error('Error:', action.error);
        });

        builder.addCase(loadJournalEntries.fulfilled, (state, action) => {
            state.journalEntries = action.payload;
        });

        builder.addCase(loadDecks.fulfilled, (state, action) => {
            state.decks = action.payload;
            if (!state.enabledDecks) {
                state.enabledDecks = [];
            }
            action.payload.forEach((deck) => {
                if (!state.enabledDecks.includes(deck.id)) {
                    state.enabledDecks.push(deck.id);
                }
            });
        });

        builder.addCase(loadCardsByDeckId.fulfilled, (state, action) => {
            const { deckId, cards } = action.payload;
            state.cardsByDeckId[deckId] = cards;
        });

        // Deck thunk error handling
        builder.addCase(createDeckThunk.rejected, (_, action) => {
            console.error('Failed to create deck:', action.error);
        });
        builder.addCase(updateDeckThunk.rejected, (_, action) => {
            console.error('Failed to update deck:', action.error);
        });
        builder.addCase(deleteDeckThunk.rejected, (_, action) => {
            console.error('Failed to delete deck:', action.error);
        });

        // Card thunk error handling
        builder.addCase(createCardThunk.rejected, (_, action) => {
            console.error('Failed to create card:', action.error);
        });
        builder.addCase(updateCardThunk.rejected, (_, action) => {
            console.error('Failed to update card:', action.error);
        });
        builder.addCase(deleteCardThunk.rejected, (_, action) => {
            console.error('Failed to delete card:', action.error);
        });

        // SRS thunk reducers
        builder.addCase(loadCurrentSRSCards.fulfilled, (state, action) => {
            state.currentSRSCards = action.payload;
        });
        builder.addCase(loadCurrentSRSCards.rejected, (_, action) => {
            console.error('Failed to load SRS cards:', action.error);
        });
        builder.addCase(submitSRSAnswer.rejected, (_, action) => {
            console.error('Failed to submit SRS answer:', action.error);
        });
    },
});

// Action creators are generated for each case reducer function
export const {
    addEntry,
    clearJournal,
    updateEntry,
    addFeedback,
    removeEntry,
    replaceTemporaryEntry,
    setModel,
    setUser,
    addDeck,
    updateDeck,
    removeDeck,
    addCard,
    updateCard,
    removeCard,
    toggleDeckEnabled,
    setShowDecksView,
    setCurrentSRSCards,
    removeCurrentSRSCard,
} = appSlice.actions;

export default appSlice.reducer;
